import React, { useRef, useState, useEffect } from "react";
import { useParams, useLocation } from "react-router-dom";
import { Toast } from "primereact/toast";
import { FileUpload } from "primereact/fileupload";
import { ProgressBar } from "primereact/progressbar";
import { Button } from "primereact/button";
import { Tag } from "primereact/tag";
import { TabView, TabPanel } from "primereact/tabview";
import { Dialog } from "primereact/dialog";
import { Password } from "primereact/password";
import api from "../../Services/api";
import { saveAs } from "file-saver";
import usePageTitle from "../usePageTitle";
import Loader from "../../AuthContext/Preloader";
import LinkNotFound from "../../Components/404LinkNotFound";
import SuccsessfullExcelSubmit from "./SuccsessfullExcelSubmit";
import "./ExcelForm.css";

const ExcelForm = () => {
  usePageTitle("Excel Form - SkeyeForm™");
  const { formId } = useParams();
  const location = useLocation();
  const [form, setForm] = useState(null);
  const [formStatus, setFormStatus] = useState("loading");
  const [totalSize, setTotalSize] = useState(0);
  const [password, setPassword] = useState("");
  const [isPasswordModalVisible, setIsPasswordModalVisible] = useState(false);
  const [actualFormId, setActualFormId] = useState(null);
  const toast = useRef(null);
  const fileUploadRef = useRef(null);
  const [showSuccessPage, setShowSuccessPage] = useState(false);
  const [isFormSubmitted, setIsFormSubmitted] = useState(false);
  const [errorDialogVisible, setErrorDialogVisible] = useState(false);
  const [validationErrors, setValidationErrors] = useState([]);

  useEffect(() => {
    if (!formId) {
      const pathParts = location.pathname.split("/");
      const lastPart = pathParts[pathParts.length - 1];

      if (lastPart && lastPart !== "form-excel") {
        setActualFormId(lastPart);
      } else {
        setFormStatus("down");
        return;
      }
    } else {
      setActualFormId(formId);
    }
  }, [formId, location]);

  useEffect(() => {
    if (!actualFormId) return;

    const fetchForm = async () => {
      try {
        // eslint-disable-next-line
        const response = await api.get(`/forms/fill/${actualFormId}`);

        if (response.data.status === "down") {
          setFormStatus("down");
        } else {
          setForm(response.data.form);
          setFormStatus("up");
          if (response.data.form.password) {
            setIsPasswordModalVisible(true);
          }
        }
      } catch (error) {
        console.error("Error fetching form:", error);
        setFormStatus("down");
        toast.current.show({
          severity: "error",
          summary: "Error",
          detail: `Failed to load form: ${error.message}`,
          life: 5000,
        });
      }
    };

    fetchForm();
  }, [actualFormId]);

  const handlePasswordSubmit = async () => {
    try {
      const response = await api.post(`/forms/check-password/${actualFormId}`, {
        password,
      });
      if (response.data.message === "Password matches") {
        setIsPasswordModalVisible(false);
        toast.current.show({
          severity: "success",
          summary: "Success",
          detail: "Password is correct",
          life: 3000,
        });
      } else if (response.data.message === "Password does not match") {
        toast.current.show({
          severity: "error",
          summary: "Error",
          detail: "Incorrect password",
          life: 3000,
        });
      }
    } catch (error) {
      if (
        error.response &&
        error.response.data.message === "Password does not match"
      ) {
        toast.current.show({
          severity: "error",
          summary: "Error",
          detail: "Incorrect password",
          life: 3000,
        });
      } else {
        toast.current.show({
          severity: "error",
          summary: "Error",
          detail: "An error occurred while checking the password",
          life: 3000,
        });
      }
    }
  };

  const onTemplateSelect = (e) => {
    let _totalSize = totalSize;
    let files = e.files;

    Object.keys(files).forEach((key) => {
      _totalSize += files[key].size || 0;
    });

    setTotalSize(_totalSize);
  };

  const onTemplateRemove = (file, callback) => {
    setTotalSize(totalSize - file.size);
    callback();
  };

  const onTemplateClear = () => {
    setTotalSize(0);
  };

  const headerTemplate = (options) => {
    const { className, chooseButton, uploadButton, cancelButton } = options;
    const value = totalSize / 100000;
    const formatedValue =
      fileUploadRef && fileUploadRef.current
        ? fileUploadRef.current.formatSize(totalSize)
        : "0 B";

    return (
      <div
        className={className}
        style={{
          backgroundColor: "transparent",
          display: "flex",
          alignItems: "center",
        }}
      >
        {chooseButton}
        {uploadButton}
        {cancelButton}
        <div className="flex align-items-center gap-3 ml-auto">
          <span>{formatedValue} / 10 MB</span>
          <ProgressBar
            value={value}
            showValue={false}
            style={{ width: "10rem", height: "12px" }}
          ></ProgressBar>
        </div>
      </div>
    );
  };

  const itemTemplate = (file, props) => {
    return (
      <div className="flex align-items-center flex-wrap">
        <div
          className="flex align-items-center"
          style={{
            width: window.innerWidth > 768 ? "40%" : "100%",
            marginBottom: window.innerWidth > 768 ? 0 : "0.5rem",
          }}
        >
          <i
            className="pi pi-file-excel"
            style={{
              fontSize: "2rem",
              color: "#107C41", // Excel green color
              marginRight: "0.5rem",
              width: "48px",
              height: "48px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          ></i>
          <span
            className="flex flex-column text-left ml-3"
            style={{ width: "calc(100% - 60px)" }}
          >
            <span
              style={{
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                maxWidth: "100%",
                display: "block",
              }}
            >
              {file.name}
            </span>
            <small>{new Date().toLocaleDateString()}</small>
          </span>
        </div>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            width: window.innerWidth > 768 ? "60%" : "100%",
          }}
        >
          <Tag
            value={props.formatSize}
            severity="warning"
            className="px-3 py-2"
          />
          <Button
            type="button"
            icon="pi pi-times"
            className="p-button-outlined p-button-rounded p-button-danger"
            onClick={() => onTemplateRemove(file, props.onRemove)}
          />
        </div>
      </div>
    );
  };

  const emptyTemplate = () => {
    return (
      <div className="flex align-items-center flex-column">
        <i
          className="pi pi-file-excel mt-3 p-5"
          style={{
            fontSize: window.innerWidth > 768 ? "5em" : "3em",
            borderRadius: "50%",
            backgroundColor: "var(--surface-b)",
            color: "#107C41", // Excel green color
          }}
        ></i>
        <span
          style={{
            fontSize: window.innerWidth > 768 ? "1.2em" : "1em",
            color: "var(--text-color-secondary)",
            textAlign: "center",
            padding: "0 1rem",
          }}
          className="my-5"
        >
          Drag and Drop Excel File Here
        </span>
      </div>
    );
  };

  const chooseOptions = {
    icon: "pi pi-fw pi-file-excel",
    iconOnly: true,
    className: "custom-choose-btn p-button-rounded p-button-outlined",
  };

  const cancelOptions = {
    icon: "pi pi-fw pi-times",
    iconOnly: true,
    className:
      "custom-cancel-btn p-button-danger p-button-rounded p-button-outlined",
  };

  const handleGenerateClick = async () => {
    if (!form) {
      toast.current.show({
        severity: "warn",
        summary: "Warning",
        detail: "Form data not available",
        life: 3000,
      });
      return;
    }

    const data = {
      id: form.id,
      title: form.title,
      label: `${form.label}#${new Date().toISOString().split("T")[0]}`,
      description: form.description,
      createdAt: new Date().toISOString(),
      properties: form.properties,
      status: "up",
      password: null,
      timer: null,
      timerStart: null,
      timerEnd: null,
    };

    try {
      const response = await api.post("/excel/generate", data, {
        responseType: "blob",
      });

      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      saveAs(blob, `${form.title}_template.xlsx`);
      toast.current.show({
        severity: "success",
        summary: "Success",
        detail: "Excel generated successfully",
        life: 3000,
      });
    } catch (error) {
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: "Error generating Excel",
        life: 3000,
      });
      console.error("Error generating Excel:", error);
    }
  };

  const handleUpload = async ({ files }) => {
    if (!form || !files.length) {
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: "Please upload a file.",
        life: 3000,
      });
      return;
    }
    const file = files[0];
    const validExcelTypes = [
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      "application/vnd.ms-excel",
    ];
    const fileExtension = file.name.split(".").pop().toLowerCase();

    if (
      !validExcelTypes.includes(file.type) ||
      !["xlsx"].includes(fileExtension)
    ) {
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: "Please upload only Excel files (.xlsx)",
        life: 3000,
      });
      return;
    }

    //const userData = JSON.parse(localStorage.getItem("userData") || "{}");

    //const userId = String(userData.id);

    const formData = new FormData();
    formData.append("file", file);
    formData.append("formSchemaId", form.id);
    formData.append("submissionId", 0);
    try {
      // eslint-disable-next-line
      const response = await api.post("/excel/upload", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      toast.current.show({
        severity: "success",
        summary: "Success",
        detail: "File uploaded successfully",
        life: 3000,
      });
      setIsFormSubmitted(true);
    } catch (error) {
      console.error("Excel upload error:");

      // Handle array of errors from the API
      if (
        error.response?.data?.errors &&
        Array.isArray(error.response.data.errors)
      ) {
        // Store errors in state and show dialog instead of toast
        setValidationErrors(error.response.data.errors);
        setErrorDialogVisible(true);
      } else {
        // Fallback to generic error toast
        toast.current.show({
          severity: "error",
          summary: "Error",
          detail: error.response?.data?.message || "Error uploading file",
          life: 3000,
        });
      }
    }
  };

  useEffect(() => {
    if (isFormSubmitted) {
      const timer = setTimeout(() => {
        setShowSuccessPage(true);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [isFormSubmitted]);

  if (formStatus === "loading") {
    return <Loader />;
  }

  if (formStatus === "down") {
    return <LinkNotFound />;
  }

  if (showSuccessPage) {
    return <SuccsessfullExcelSubmit />;
  }

  const errorDialogFooter = (
    <div
      style={{
        display: "flex",
        justifyContent: "center",
        padding: window.innerWidth > 768 ? "1rem" : "0.75rem",
        width: "100%",
      }}
    >
      <Button
        label="Close"
        icon="pi pi-times"
        onClick={() => setErrorDialogVisible(false)}
        className="p-button-text"
        style={{
          padding: window.innerWidth > 768 ? "0.75rem 1.5rem" : "0.75rem 1rem",
          fontSize: window.innerWidth > 768 ? "1rem" : "0.9rem",
          width: window.innerWidth > 768 ? "auto" : "90%",
          margin: "0 auto",
        }}
      />
    </div>
  );

  return (
    <div className="p-grid p-dir-col table-page" style={{ width: "100%" }}>
      <Toast ref={toast} />
      <Dialog
        header="Enter Password"
        visible={isPasswordModalVisible}
        modal
        closable={false}
        dismissableMask={false}
        style={{ width: "20vw" }}
      >
        <div className="p-field p-fluid">
          <Password
            toggleMask
            id="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            feedback={false}
          />
        </div>

        <div style={{ display: "flex", justifyContent: "flex-end" }}>
          <Button
            label="Submit"
            onClick={handlePasswordSubmit}
            style={{
              backgroundColor: "#e29b45",
              color: "#fff",
              border: "none",
            }}
          />
        </div>
      </Dialog>
      <Dialog
        header="Excel Validation Errors"
        visible={errorDialogVisible}
        style={{
          width: window.innerWidth > 768 ? "80vw" : "98vw",
          height: window.innerWidth > 768 ? "80vh" : "95vh",
          maxWidth: window.innerWidth > 768 ? "none" : "98vw",
        }}
        className="excel-error-dialog"
        footer={errorDialogFooter}
        onHide={() => setErrorDialogVisible(false)}
        modal
      >
        {validationErrors.length > 0 && (
          <div>
            <p className="text-lg font-bold mb-3">
              Please correct the following errors in your Excel file:
            </p>
            <div
              className="border-1 surface-border border-round p-3"
              style={{
                maxHeight: window.innerWidth > 768 ? "50vh" : "70vh",
                overflow: "auto",
                border: "1px solid #dee2e6",
                borderRadius: "6px",
                fontSize: window.innerWidth > 768 ? "1rem" : "0.9rem",
              }}
            >
              <ul className="list-none p-0 m-0">
                {validationErrors.map((error, index) => (
                  <li
                    key={index}
                    className="p-2 border-bottom-1 surface-border flex align-items-center"
                  >
                    <i className="pi pi-exclamation-circle text-red-500 mr-2"></i>
                    <span>{error}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}
      </Dialog>
      <div
        className="p-col title"
        style={{
          display: "flex",
          alignItems: "center",
        }}
      >
        <img
          src="/img/majesteye-icon-skeye-form-horizontal-white-background.svg"
          alt="SkeyeForm Logo"
          style={{
            height: "50px",
            alignSelf: "flex-start",
            marginBottom: "2rem",
          }}
        />
        <h1 style={{ margin: 0, fontSize: "1.5rem" }}>Excel Form</h1>
        <div className="title-bar"></div>
      </div>
      <div
        className="table-container"
        style={{ width: window.innerWidth > 768 ? "90%" : "98%" }}
      >
        <div className="p-col" style={{ width: "100%" }}>
          {form && (
            <>
              <h2
                style={{
                  textAlign: "center",
                  fontFamily: "Poppins",
                  color: "#0D2E4D",
                }}
              >
                {form.title}
              </h2>
              <TabView>
                <TabPanel
                  header="Generate Excel"
                  leftIcon="pi pi-file-excel mr-2 mb-2"
                >
                  <p>
                    Generate an Excel template for <strong>{form.title}</strong>
                  </p>
                  <div style={{ display: "flex", justifyContent: "flex-end" }}>
                    <Button
                      label="Generate"
                      onClick={handleGenerateClick}
                      style={{ backgroundColor: "#0d2e4d", border: "none" }}
                    />
                  </div>
                </TabPanel>
                <TabPanel
                  header="Upload Excel"
                  leftIcon="pi pi-upload mr-2 mb-2"
                >
                  <p>
                    Upload a completed Excel file for{" "}
                    <strong>{form.title}</strong>
                  </p>
                  <FileUpload
                    ref={fileUploadRef}
                    customUpload
                    uploadHandler={handleUpload}
                    name="excel[]"
                    multiple
                    accept=".xlsx,.xls"
                    maxFileSize={1000000}
                    onSelect={onTemplateSelect}
                    onError={onTemplateClear}
                    onClear={onTemplateClear}
                    headerTemplate={headerTemplate}
                    itemTemplate={itemTemplate}
                    emptyTemplate={emptyTemplate}
                    chooseOptions={chooseOptions}
                    cancelOptions={cancelOptions}
                    required
                    className="excel-file-upload"
                    thumbnailTemplate={null}
                    uploadOptions={{
                      className: "p-button p-component p-disabled",
                      icon: "none",
                      style: {
                        backgroundColor: "#fff",
                        color: "#fff",
                        border: "none",
                        width: "0px",
                        height: "0px",
                      },
                    }}
                  />
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "flex-end",
                      marginTop: "1em",
                    }}
                  >
                    <Button
                      label="Submit"
                      style={{ backgroundColor: "#0d2e4d", border: "none" }}
                      onClick={() => fileUploadRef.current.upload()}
                    />
                  </div>
                </TabPanel>
              </TabView>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ExcelForm;
